import { useState, useRef, useCallback, useEffect } from 'react';
import { getIdToken } from '@helios/auth-firebase-web';
import {
  MAX_RECORDING_DURATION_S,
  transcribeAudioBlob,
  type VttMode,
  type VttSummaryStyle,
} from '../voice/vtt-support';
import { blobToBase64, pickSupportedMimeType } from '../voice/vtt-recording';

// ─── Types ───

export type { VttSummaryStyle } from '../voice/vtt-support';

export type VttStatus = 'idle' | 'recording' | 'transcribing' | 'error';

/** In-memory handle to the just-completed recording (PRD.271). */
export interface VttRecording {
  /** The recorded audio blob. Persist this to storage; do not discard. */
  blob: Blob;
  /** Actual negotiated MIME type (Safari/iOS may differ from webm). */
  mimeType: string;
  /** Recording duration in milliseconds (recorder start → stop). */
  durationMs: number;
  /** Raw transcript returned for this recording (may be null on failure). */
  transcript: string | null;
}

export interface UseVoiceToTextReturn {
  status: VttStatus;
  error: string | null;
  /** Seconds elapsed since recording started (ticks every second). */
  elapsed: number;
  /** Maximum recording duration in seconds. */
  maxDuration: number;
  /** Object URL of the last recording, for playback debugging. Null until first recording completes. */
  lastRecordingUrl: string | null;
  /**
   * In-memory handle to the last completed recording (blob + mime + duration).
   * Preserved so the caller can upload + persist it (PRD.271). Null until the
   * first recording completes.
   */
  lastRecording: VttRecording | null;
  /** Start recording from the mic. */
  start: () => void;
  /** Stop recording and send audio to backend. Returns the transcript text on success. */
  stop: () => Promise<string | null>;
  /** Play back the last recording in the browser. */
  playLastRecording: () => void;
  /** Reset error state back to idle. */
  reset: () => void;
}

// ─── Config ───

const VTT_URL = import.meta.env.VITE_VOICE_TO_TEXT_URL;

// ─── Hook ───

export interface UseVoiceToTextOptions {
  /** Voice pipeline mode. Defaults to 'transcription'. */
  mode?: VttMode;
  /**
   * If set, the backend will summarize the recognized text in this style
   * before returning. Used for field-specific output like Chief Complaint.
   */
  summaryStyle?: VttSummaryStyle;
  /** Fired whenever a result completes — handles both manual stop and auto-stop. */
  onResult?: (text: string) => void;
  /**
   * Fired after a recording completes with the retained audio blob + metadata
   * (PRD.271). Called on transcription success so the caller can persist the
   * recording and its raw transcript. Not fired for empty/failed recordings.
   */
  onRecordingComplete?: (recording: VttRecording) => void;
}

/**
 * Hook overloads:
 *  - useVoiceToText(options)  ← preferred
 *  - useVoiceToText(mode, onResult)  ← legacy form, still supported
 */
export function useVoiceToText(options?: UseVoiceToTextOptions): UseVoiceToTextReturn;
export function useVoiceToText(
  mode: VttMode,
  onResult?: (text: string) => void,
): UseVoiceToTextReturn;
export function useVoiceToText(
  modeOrOptions?: VttMode | UseVoiceToTextOptions,
  legacyOnResult?: (text: string) => void,
): UseVoiceToTextReturn {
  const opts: UseVoiceToTextOptions =
    typeof modeOrOptions === 'string'
      ? { mode: modeOrOptions, onResult: legacyOnResult }
      : (modeOrOptions ?? {});
  const mode: VttMode = opts.mode ?? 'transcription';
  const summaryStyle = opts.summaryStyle;
  const onResult = opts.onResult;
  const onRecordingComplete = opts.onRecordingComplete;
  const [status, setStatus] = useState<VttStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [lastRecordingUrl, setLastRecordingUrl] = useState<string | null>(null);
  const [lastRecording, setLastRecording] = useState<VttRecording | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoStopRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stopRef = useRef<() => Promise<string | null>>(null!);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const onRecordingCompleteRef = useRef(onRecordingComplete);
  onRecordingCompleteRef.current = onRecordingComplete;
  // Actual negotiated MIME type + recorder start time, so the blob, the
  // backend request, and the retained recording all agree (Safari/iOS differ).
  const mimeTypeRef = useRef<string>('audio/webm');
  const startedAtMsRef = useRef<number>(0);

  const cleanupTimers = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (autoStopRef.current) {
      clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
    setElapsed(0);
  }, []);

  const cleanup = useCallback(() => {
    cleanupTimers();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, [cleanupTimers]);

  const start = useCallback(() => {
    if (status === 'recording') return;

    setError(null);

    // Browser-support + Safari/iOS MIME negotiation (PRD.271). Fail fast with a
    // clear message rather than throwing an opaque MediaRecorder error.
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('Voice recording is not supported in this browser');
      setStatus('error');
      return;
    }
    const mimeType = pickSupportedMimeType();
    if (!mimeType) {
      setError('Voice recording is not supported in this browser');
      setStatus('error');
      return;
    }
    mimeTypeRef.current = mimeType;

    setStatus('recording');
    setElapsed(0);
    chunksRef.current = [];

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType });
        mediaRecorderRef.current = recorder;
        startedAtMsRef.current = Date.now();

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start();

        timerRef.current = setInterval(() => {
          setElapsed((prev) => prev + 1);
        }, 1000);

        autoStopRef.current = setTimeout(() => {
          stopRef.current();
        }, MAX_RECORDING_DURATION_S * 1000);
      })
      .catch((err) => {
        console.error('[voice-to-text] Mic access denied:', err);
        setError('Microphone access denied');
        setStatus('error');
        cleanup();
      });
  }, [status, cleanup]);

  const stop = useCallback(async (): Promise<string | null> => {
    cleanupTimers();
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') {
      cleanup();
      setStatus('idle');
      return null;
    }

    const recordingMime = mimeTypeRef.current;
    const durationMs = startedAtMsRef.current ? Date.now() - startedAtMsRef.current : 0;
    const audioBlob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        resolve(new Blob(chunksRef.current, { type: recordingMime }));
      };
      recorder.stop();
    });

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }

    // Store blob URL for playback debugging + retain the blob for persistence.
    if (lastRecordingUrl) URL.revokeObjectURL(lastRecordingUrl);
    const blobUrl = URL.createObjectURL(audioBlob);
    setLastRecordingUrl(blobUrl);
    setLastRecording({ blob: audioBlob, mimeType: recordingMime, durationMs, transcript: null });

    if (audioBlob.size === 0) {
      setStatus('idle');
      cleanup();
      return null;
    }

    setStatus('transcribing');

    try {
      const base64 = await blobToBase64(audioBlob);

      const result = await transcribeAudioBlob(
        { audioBase64: base64, mimeType: recordingMime, mode, summaryStyle },
        { endpointUrl: VTT_URL, getToken: getIdToken },
      );

      setStatus('idle');
      cleanup();
      // The immutable raw transcript is the pre-summary text when summarized;
      // otherwise `text` already is the raw transcript. `onResult` still gets
      // the summarized `text` for the editable Chief Complaint.
      const rawTranscript = result.rawText ?? result.text;
      const recording: VttRecording = {
        blob: audioBlob,
        mimeType: recordingMime,
        durationMs,
        transcript: rawTranscript,
      };
      setLastRecording(recording);
      onResultRef.current?.(result.text);
      onRecordingCompleteRef.current?.(recording);
      return result.text;
    } catch (err) {
      // Do NOT log the error object — it may carry transcript/audio detail.
      const msg = err instanceof Error ? err.message : 'Transcription failed';
      setError(msg);
      setStatus('error');
      cleanup();
      return null;
    }
  }, [mode, summaryStyle, cleanup, cleanupTimers, lastRecordingUrl]);

  stopRef.current = stop;

  useEffect(() => cleanup, [cleanup]);

  const playLastRecording = useCallback(() => {
    if (!lastRecordingUrl) return;
    const audio = new Audio(lastRecordingUrl);
    audio.play().catch((err) => console.error('[voice-to-text] Playback failed:', err));
  }, [lastRecordingUrl]);

  const reset = useCallback(() => {
    setError(null);
    setStatus('idle');
    cleanup();
  }, [cleanup]);

  return {
    status,
    error,
    elapsed,
    maxDuration: MAX_RECORDING_DURATION_S,
    lastRecordingUrl,
    lastRecording,
    start,
    stop,
    playLastRecording,
    reset,
  };
}
